import { useCallback, useMemo, useRef } from "react";

import { Buffer } from "buffer";
import {
  compressToEncodedURIComponent,
  decompressFromEncodedURIComponent,
} from "lz-string";
import { DeepPartial } from "react-hook-form";

import { captureException } from "src/lib/monitoring";
import { NavigateOptions, useSearchParams } from "src/router";

/**
 * Encode an object to a string using Base64 encryption.
 */
export const encode = <T>(value: T) => {
  return compressToEncodedURIComponent(JSON.stringify(value));
};

/**
 * Decode a string from Base64 format to an object.
 */
export const decode = <T>(encodedValue: string): T => {
  try {
    return JSON.parse(decompressFromEncodedURIComponent(encodedValue));
  } catch {
    console.error("Error decompressing URL state, falling back to decoding");
    return JSON.parse(Buffer.from(encodedValue, "base64").toString("utf8"));
  }
};

export const useEncodedSearchParams = <T extends Record<string, unknown>>(
  key = "data",
): [
  T | null,
  (value: DeepPartial<T>, navigateOptions?: NavigateOptions) => void,
] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlValue = searchParams.get(key);
  const previousValueRef = useRef<T | null>(null);

  const get = useCallback((): T | null => {
    try {
      return urlValue !== null ? decode<T>(urlValue) : urlValue;
    } catch (error) {
      captureException(error, { extra: { key, urlValue } });
      return null;
    }
  }, [urlValue, key]);

  const set = useCallback(
    (value: DeepPartial<T>, options?: NavigateOptions) => {
      const newValue = { ...previousValueRef.current, ...value };

      if (
        JSON.stringify(newValue) !== JSON.stringify(previousValueRef.current)
      ) {
        const newParams = new URLSearchParams(searchParams.toString());

        newParams.set(key, encode(newValue));
        setSearchParams(newParams, { replace: true, ...options });

        previousValueRef.current = newValue as T;
      }
    },
    [searchParams, setSearchParams, key],
  );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const data = useMemo(() => {
    const newData = get();
    previousValueRef.current = newData;
    return newData;
  }, [get, location.search]);

  return useMemo(() => [data, set], [data, set]);
};
